// ─────────────────────────────────────────────
//  secondProvider — THE ONE ANSWER TO "who else is in this room?"
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  A room may hold TWO providers. It gets there two ways:
//
//    1. THE SHEET SAYS SO — "Trevor Stamand 5 orient Kayla Gibbons" is a
//       preceptor and an orientee standing in one room (domain/orientation).
//    2. SOMEBODY ASSIGNS ONE — the AIC picks a CRNA off today's roster and
//       drops them into a room to help or supervise (timeline quick actions).
//
//  ⚠️ BOTH WRITE THE SAME FIELDS. `orienteeId` / `orienteeName` /
//  `orienteeShiftCode` / `orienteeDepartTime` are the ONE home for "the second
//  person in this room". A parallel `helperId`/`helperName` pair was considered
//  and rejected: this repo has been burned repeatedly by two homes for one fact
//  (mdCallHome, givingBreaks, the half-converted import pipeline), and the
//  failure mode is always the same — one surface reads the home the other did
//  not write, and renders NOTHING with no error. Every board already in
//  Firestore uses these field names; a second pair would mean every consumer
//  had to remember to check both, forever.
//
//  ⚠️ WHAT GENUINELY DIFFERS IS THE *RELATIONSHIP*, NOT THE PERSON. An orientee
//  is being taught; a helping CRNA is not, and labelling them as a trainee in
//  front of the department would be wrong.
//
//  ⚠️ NOTHING ON SCREEN SHOWS THAT DIFFERENCE ANY MORE (owner, 2026-08-20:
//  "i dont care who is orienting who, place the 2 crnas"). The bar, the
//  adequacy card and the spoken label all name two CRNAs evenly. The role is
//  kept because a WRITE still needs it — replacing a helping colleague must not
//  relabel them as somebody's trainee. So exactly ONE extra field carries
//  that difference — `secondProviderRole` — and it is named for what it now
//  covers rather than for orientation. Absent means "orienting", because every
//  row written before this shipped came from an "X Orient Y" cell.
//
//  ⚠️ AND SINCE 2026-08-20 A SECOND PROVIDER IS COVER. The product owner was
//  asked directly, with the 2026-08-14 incident named, and chose it: two CRNAs
//  in a room are two CRNAs. The exclusion that used to key off `orienting` is
//  gone. Counting is driven by ONE per-person field, `countsAsCover`, so a
//  future "students do not count" rule is a flag on the member and not a
//  rewrite of buildStaffing. `orienting` survives for LABELLING and for the
//  seat rule only — the preceptor still holds the room, and `isSoloCase` still
//  reads a two-CRNA room as anything but empty.
//
//  Pure: no Firebase, no React. Assignment rows in, people out.
// ─────────────────────────────────────────────
import { orientationOf, seatName } from "./orientation";
import { coProviderSegments, pairedSegments, splitNameAndShift } from "./providerCell";

/** Why the second person is in the room. Absent on a row means ORIENTING. */
export const SECOND_PROVIDER_ROLES = {
  ORIENTING: "orienting",
  HELPING: "helping",
};

// Name key for matching one person across the roster, the assignment rows and
// the break board. Roster names are "Josh Larson"; the importer writes
// assignment names as "Josh Larson, CRNA".
//
// ⚠️ DELIBERATELY A LOCAL COPY of the rule in timeline.js (`rosterNameKey`) and
// useBreakBoardRows (`nameKey`). Importing either would make this module and
// timeline.js circular — timeline.js imports THIS one. The rule is three lines
// and is pinned by a test here; if it ever needs to change, change it in all
// three and the test will say so.
const nameKey = (s) =>
  String(s || "").split(",")[0].trim().toLowerCase().replace(/^dr\.?\s+/, "");

// "HH:MM" → minutes past midnight, or null. Local rather than imported from
// timeline.js for the circularity reason above.
function hm(text) {
  const m = String(text || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// ── THE SHIFT TOKEN THAT WAS SITTING IN THE SENTENCE ────────────
//
// ⚠️ THIS IS WHY A PAIRED ROOM SHOWED NO LEAVING TIME FOR ANYBODY. On a board
// imported BEFORE the pair-aware parser shipped, the whole cell landed on
// `ahpName` and `ahpShiftCode` was written **null** — measured, not assumed:
// the old `parseCrnaCell` looked for a trailing shift token, and
// "Trevor Stamand 5 orient Kayla Gibbons" ends in a surname, so its
// `shiftNumber` came back null and `buildBoardWrites` stored
// `ahpShiftCode: row.ahpShiftCode ?? null`. Mike Peterson's row, one seat over,
// ends in "5" and got `ahpShiftCode: "5"` — which is the entire difference
// between the two bars the owner photographed.
//
// The `5` is IN THE CELL. Recovering it is reading what the sheet printed, not
// guessing: it is the same fallback `orientationOf` already plays for the NAME,
// applied to the token beside it.
//
// ⚠️ RECOVERY IS RESTRICTED TO CELLS THAT NAME TWO PEOPLE, on purpose. A
// single-provider cell already had its code parsed at import; reaching into an
// arbitrary `ahpName` would let "TOR 16" or any other non-person string mint a
// departure hour. Only the shape that is actually broken is touched, so every
// single-provider row stays byte-identical.
//
// ⚠️ AND THE RAW CODE, NEVER THE NUMBER. `splitNameAndShift` hands back the
// token as written, so "7p-7a" survives as "7p-7a". Flattened to 7 it reads
// back through `shiftCodeEndHour24` as 7 PM — twelve hours wrong for a night
// CRNA (pt.156).

/**
 * The RAW shift code for the person HOLDING this room.
 *
 * The structured field ALWAYS wins: `ahpShiftCode` is what a human edit and a
 * modern import write, and text once printed on a sheet must never outrank it.
 * Only when there is none is the seat's own side of the cell read.
 *
 * Returns null when nothing names a code — which is honest, and makes the bar
 * print a name with no time rather than somebody else's hour.
 */
export function seatShiftCode(assignment) {
  if (!assignment) return null;
  const structured = assignment.ahpShiftCode;
  if (structured != null && structured !== "") return String(structured);
  const sides = pairedSegments(assignment.ahpName);
  if (!sides) return null;
  return splitNameAndShift(sides[0]).shiftCode || null;
}

// The second person's OWN token, from THEIR side of the cell — never the
// seat's. Matched by name before it is used, so a row whose structured
// `orienteeName` names somebody the sentence does not (a human swapped who is
// standing there) recovers nothing instead of inheriting a stranger's hour.
function recoveredSecondShiftCode(assignment, name) {
  const sides = pairedSegments(assignment?.ahpName);
  if (!sides) return null;
  const { name: sideName, shiftCode } = splitNameAndShift(sides[1]);
  if (!shiftCode) return null;
  const k = nameKey(name);
  return k && nameKey(sideName) === k ? shiftCode : null;
}

/**
 * THE ONE RESOLVER. Who is the second provider in this room?
 *
 * Import THIS — do not re-derive it from `orienteeName`, from the raw `ahpName`
 * cell, or from a quick-action's own local state. The chart, the room modal,
 * the roster, the break board and the staffing count must all be answering the
 * same question with the same code.
 *
 * @param {object|null} assignment  an assignment row
 * @returns {null | {
 *   id: string|null,        // staff/user id when the importer resolved one
 *   key: string,            // stable identity, synthetic when there is no id
 *   name: string,
 *   role: "orienting"|"helping",
 *   orienting: boolean,     // role === "orienting" — for LABELS, not counting
 *   shiftCode: string|null, // their OWN shift, never the seat holder's
 *   departTime: string,     // "HH:MM" when a human set one explicitly
 *   departMin: number|null, // minutes past midnight for the above, else null
 * }}
 *
 * ⚠️ THE LEGACY FALLBACK IS WHAT MAKES TODAY'S BOARD WORK. Every row already in
 * Firestore was imported before the structured fields existed: it carries the
 * mashed cell on `ahpName` and nothing else. `orientationOf` re-derives the
 * pair from that string, so those rooms answer this question correctly with no
 * re-import and no backfill.
 */
export function secondProviderOf(assignment) {
  if (!assignment) return null;
  const orienting = orientationOf(assignment);
  // ⚠️ A PAIR THE SHEET NEVER CALLED AN ORIENTATION. "X/Y" and "X (Y)" are the
  // other two spellings, and on a board ALREADY IN FIRESTORE they are all that
  // exists — the row carries the mashed cell on `ahpName` and no structured
  // fields. Recovering them here is what puts those people on the roster, the
  // break board and the staffing count with no write to a live board, exactly
  // as the `orientationOf` fallback does for "X orient Y".
  //
  // Asked SECOND, so an explicit orientee always wins: "orient" carries a
  // teaching relationship and a slash does not.
  if (!orienting) return coProviderFallback(assignment);
  let name = String(orienting.orienteeName || "").trim();
  if (!name) return null;

  const role =
    assignment.secondProviderRole === SECOND_PROVIDER_ROLES.HELPING
      ? SECOND_PROVIDER_ROLES.HELPING
      : SECOND_PROVIDER_ROLES.ORIENTING;

  const id = orienting.orienteeId || null;
  let shiftCode =
    assignment.orienteeShiftCode == null || assignment.orienteeShiftCode === ""
      ? null
      : String(assignment.orienteeShiftCode);

  // ⚠️ THE SECOND NAME CAN CARRY ITS OWN CODE, AND THE LEGACY PATH KEPT IT
  // INSIDE THE NAME. `orientationOf` strips a trailing number off the PRECEPTOR
  // and hands the orientee half back untouched, so
  // "Bob Schneider 5 orient Kelsey Congress 3" resolved a colleague literally
  // called "Kelsey Congress 3" — her shift printed as part of her name and
  // available to nothing that needed it. Structured fields are not touched:
  // this runs only when the row has no `orienteeName` of its own, i.e. the name
  // came out of the sentence in the first place.
  //
  // ⚠️ THE SHIFT IS RECOVERED ONLY WHEN A TOKEN IS REALLY THERE. "Kayla
  // Gibbons" and "Shannon" recover NOTHING — a missing token means no
  // departure, which is the honest answer. Borrowing Trevor's 5 for Kayla would
  // tell a charge nurse a room keeps a person it is about to lose, or loses one
  // it keeps.
  //
  // ⚠️ THE NAME, THOUGH, IS ALWAYS CLEANED. "Ashley Weider 5 orient Lester
  // 7-8:30" resolved a colleague called "Lester 7-8:30" — the sheet's note
  // about his own morning, rendered as part of his name. `cleanProviderName` is
  // the ONE definition of "the name text in this cell"; a second rule here
  // would drift from the importer's.
  if (!String(assignment.orienteeName || "").trim()) {
    const split = splitNameAndShift(name);
    if (split.name) {
      name = split.name;
      if (!shiftCode) shiftCode = split.shiftCode;
    }
  }
  if (!shiftCode) shiftCode = recoveredSecondShiftCode(assignment, name);

  const departTime = String(assignment.orienteeDepartTime || "").trim();

  return {
    id,
    key: id || `sp:${nameKey(name)}`,
    name,
    role,
    orienting: role === SECOND_PROVIDER_ROLES.ORIENTING,
    shiftCode,
    departTime,
    departMin: hm(departTime),
  };
}

// The second person named by a slash or a parenthesis cell. Their OWN shift
// code rides with them — "Hana Alsamman 7a-7p/Toni Abraham 7p-7a" is two
// different leaving times, and borrowing one for the other would put a
// fabricated hour against a real person.
//
// `HELPING`, never `ORIENTING`: the sheet claimed no teaching here, only two
// colleagues in one room, and labelling one of them somebody's trainee in front
// of the department would be an invention.
function coProviderFallback(assignment) {
  const segments = coProviderSegments(assignment.ahpName);
  if (!segments || segments.length < 2) return null;
  const { name, shiftCode: fromCell } = splitNameAndShift(segments[1]);
  if (!name) return null;
  // ⚠️ THE STRUCTURED FIELDS STILL WIN HERE. This branch runs when the row has
  // no `orienteeName` — but `setSecondDepartPatch` deliberately writes ONLY
  // `orienteeDepartTime`, and a human can set `orienteeShiftCode` on a row whose
  // seat cell is still the raw sentence. Reading the cell first would throw away
  // the edit and hand the roster a leaving time the operator had already
  // corrected.
  const structuredCode =
    assignment.orienteeShiftCode == null || assignment.orienteeShiftCode === ""
      ? null
      : String(assignment.orienteeShiftCode);
  const departTime = String(assignment.orienteeDepartTime || "").trim();
  return {
    // The cell resolved nobody — that is why the pair was invisible — so there
    // is no id, and the key is minted from the name like any other.
    id: null,
    key: `sp:${nameKey(name)}`,
    name,
    role: SECOND_PROVIDER_ROLES.HELPING,
    orienting: false,
    shiftCode: structuredCode || fromCell || null,
    departTime,
    departMin: hm(departTime),
  };
}

/**
 * The other half of the same question: who HOLDS this room?
 *
 * Lives here, next to `secondProviderOf`, because the two answers are one fact
 * — "Trevor Stamand 5 orient Kayla Gibbons" is a seat and a second person, and
 * a surface that splits them with two unrelated helpers eventually prints the
 * whole sentence in one place and half of it in another. `timeline.crnaSeatName`
 * delegates here rather than keeping a second copy.
 *
 * Accepts any of the shapes the callers hold: an assignment row (`ahpName`,
 * possibly the mashed cell), a `buildTimeline` case (`provider` raw), or a
 * `placed` entry (`crnaName`, already the seat).
 */
export function roomSeatName(row) {
  if (!row) return "";
  const already = String(row.crnaName || "").trim();
  if (already) return already;
  const seat = seatName(row);
  // ⚠️ AND THE OTHER TWO SPELLINGS OF A PAIR. `seatName` splits "X orient Y"
  // and hands back anything else untouched — so a cell reading
  // "Yarsellis Frometa 5/Titi Somade 3" was printed WHOLE as the seat holder's
  // name, on the bar, in the staffing card and in the "who is leaving" list.
  // One person's row, labelled with two people's names.
  const co = coProviderSegments(seat);
  if (co) return splitNameAndShift(co[0]).name || seat;
  if (seat) return seat;
  return String(row.provider || "").trim();
}

/**
 * The exact field bag to WRITE onto an assignment row when somebody is put in a
 * room as the second provider.
 *
 * Exported so the quick-actions menu, the room modal and the importer all spell
 * these four field names once, in one place. A caller that hand-rolls the
 * object is one typo away from a person who exists in Firestore and renders
 * nowhere.
 */
export function secondProviderFields({
  id = null,
  name = "",
  shiftCode = null,
  departTime = "",
  role = SECOND_PROVIDER_ROLES.ORIENTING,
  // ⚠️ PROVENANCE, NOT A TIME. True when the shift code above was BORROWED from
  // the preceptor because the sheet printed no number beside the trainee —
  // owner, 2026-09-07: *"no number assume is 3pm"*. The hour is real enough to
  // schedule against and is NOT real enough to assert, so every surface that
  // prints it has to be able to say which it is. Absent means the sheet said it.
  shiftInherited = false,
} = {}) {
  const clean = String(name || "").trim();
  if (!clean) return CLEARED_SECOND_PROVIDER;
  return {
    orienteeId: id || null,
    orienteeName: clean,
    orienteeShiftCode: shiftCode == null || shiftCode === "" ? null : String(shiftCode),
    orienteeShiftInherited: Boolean(shiftInherited),
    orienteeDepartTime: String(departTime || ""),
    secondProviderRole:
      role === SECOND_PROVIDER_ROLES.HELPING
        ? SECOND_PROVIDER_ROLES.HELPING
        : SECOND_PROVIDER_ROLES.ORIENTING,
  };
}

/** The field bag that REMOVES the second provider from a room. */
export const CLEARED_SECOND_PROVIDER = Object.freeze({
  orienteeId: null,
  orienteeName: "",
  orienteeShiftCode: null,
  // Goes with the code it describes. Left behind, it would claim the SEAT
  // holder's hour was assumed the moment a trainee left the room.
  orienteeShiftInherited: false,
  orienteeDepartTime: "",
  secondProviderRole: null,
});

// ── COUNTING ────────────────────────────────
//
// ⚠️ ONE FIELD DECIDES WHO COUNTS. `buildStaffing` asks this and nothing else.
// Absent means YES, so every roster member already in Firestore keeps counting
// exactly as they do today and no migration is needed to flip the 2026-08-20
// decision on. A future "SRNAs/students do not count" rule sets
// `countsAsCover: false` on that member at the point they are enrolled — one
// line at one write site, not a new branch in the adequacy maths.
export const countsAsCover = (member) => member?.countsAsCover !== false;

// ⚠️ AND SENDING SOMEBODY HOME IS THE OTHER WAY TO STOP COUNTING.
// `countsAsCover` answers "is this KIND of person cover" (a student is not);
// `departed` answers "is this person still HERE" — the charge nurse pressed
// Home and they have left the building. Both had to be asked and only the
// first was: the adequacy strip went on counting a provider who had gone home,
// on the screen that decides whether a room is safe to keep running. Overcount
// is the dangerous direction, and it is the one this produced.
//
// The flag lives on the ROSTER DOC as `departed: { [memberId]: true }`, not on
// the member, so it has to be merged on before the maths sees it — the same
// shape DailyAssignmentPage already uses (`departed: !!departedMap[m.id]`) and
// BreakEnginePanel already reads (`!m.departed`). `withDeparted` is that merge,
// so the two timeline call sites cannot spell it differently.
export const isDeparted = (member) => member?.departed === true;

/** Roster members with the roster doc's `departed` map folded onto each one. */
export const withDeparted = (members, departedMap) => {
  const map = departedMap && typeof departedMap === "object" ? departedMap : {};
  return (Array.isArray(members) ? members : []).map((m) =>
    m && m.id != null && map[m.id] ? { ...m, departed: true } : m
  );
};

/**
 * A second provider as a ROSTER MEMBER.
 *
 * ⚠️ THE `id` MAY BE SYNTHETIC. A board imported before the second-provider
 * fields existed names the person and resolves no id, and a member with `id:
 * undefined` is worse than useless: `departed[undefined]` collides for every
 * such person and React renders them under one key. `secondProviderOf` hands
 * back a stable `key` derived from the name, prefixed `sp:` so it can never be
 * mistaken for — or collide with — a Firestore document id.
 *
 * `derived: true` marks a member the importer never wrote, so a surface that
 * must not offer a real write (messaging, a profile link) can tell.
 */
export function secondProviderMember(sp) {
  if (!sp || !sp.name) return null;
  return {
    id: sp.key,
    name: sp.name,
    subRole: "crna",
    shiftCode: sp.shiftCode,
    // Departure the roster reader (timeline.rosterCrnaDepartMin) can use when
    // the sheet printed no shift code but a human typed a leaving time.
    departTime: sp.departTime || "",
    // A STORAGE SLOT, NOT A RANK. Nothing renders it: no `↳`, no indent, no
    // wording on the card (owner, 2026-08-20). It exists so a write knows which
    // of the two people it is changing.
    orienting: sp.orienting,
    secondProviderRole: sp.role,
    // §0.1, product owner 2026-08-20: both CRNAs in a room are cover.
    countsAsCover: true,
    derived: true,
  };
}

/**
 * Today's CRNA roster PLUS every second provider the day's assignment rows name
 * who is not already on it.
 *
 * ⚠️ THIS IS WHAT MAKES THE THREE MISSING PEOPLE APPEAR ON A BOARD THAT IS
 * ALREADY IN FIRESTORE. Enrolment at import time (buildBoardWrites,
 * DailyAssignmentPage.onApplyRows) only fixes boards imported AFTER it shipped;
 * every board built before that names the second CRNA on an assignment row and
 * on no roster. Brian McCloud, Sam Gongora and Kayla Gibbons were on the
 * timeline and on neither the daily roster nor the Breaks panel for exactly
 * that reason. Deriving the member at READ time is the same trick
 * `orientationOf`'s legacy fallback already plays, and it needs no write to a
 * live hospital board.
 *
 * Idempotent: a person already on the roster (by id OR by name — a missing id
 * is not an absent person) is never added twice, so applying this at several
 * layers is safe.
 */
export function mergeSecondProviders(rosterCrnas = [], assignments = []) {
  const list = (Array.isArray(rosterCrnas) ? rosterCrnas : []).filter(Boolean);
  const rows = (Array.isArray(assignments) ? assignments : []).filter(Boolean);
  if (!rows.length) return list;

  const seenIds = new Set();
  const seenNames = new Set();
  for (const m of list) {
    if (m.id) seenIds.add(m.id);
    const k = nameKey(m.name);
    if (k) seenNames.add(k);
  }

  const out = [...list];
  for (const row of rows) {
    const sp = secondProviderOf(row);
    if (!sp) continue;
    const k = nameKey(sp.name);
    if (sp.id && seenIds.has(sp.id)) continue;
    if (k && seenNames.has(k)) continue;
    const member = secondProviderMember(sp);
    if (!member) continue;
    out.push(member);
    seenIds.add(member.id);
    if (k) seenNames.add(k);
  }
  return out;
}

/**
 * Every second provider named across the day's rows, deduped — the shape a
 * backfill or an enrolment loop wants.
 */
export function secondProviderMembers(assignments = []) {
  return mergeSecondProviders([], assignments);
}
